import { victorySubtext, type GameState } from "@dailywars/core/engine";
import type { MedalTier } from "@dailywars/core/medals";
import { Link, useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { MedalIcon } from "#/components/MedalIcon";
import { PremiumUpgradePopover } from "#/components/PremiumUpgradePopover";
import { Button } from "#/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "#/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "#/components/ui/dialog";
import { REPLAY_REQUIRES_PREMIUM_COPY } from "#/lib/mapAccessUpsellCopy";
import { cn } from "#/lib/utils";

type GameOverObjectiveState = Pick<
  GameState,
  "victoryConditions" | "captureCityTarget" | "tiles" | "units" | "initialSupplyTruckCount"
>;

interface GameOverBannerProps {
  status: Exclude<GameState["status"], "in_progress">;
  dayNumber: number;
  mapId: string;
  state: GameOverObjectiveState;
  onRestart: () => void;
  pending?: boolean;
  restartLabel?: string;
  pendingRestartLabel?: string;
  restartLocked?: boolean;
  showLeaderboardLink?: boolean;
  extraContent?: ReactNode;
  medalTier?: MedalTier | null;
}

function VictoryMedalCorners({ tier }: { tier?: MedalTier | null }) {
  if (!tier) return null;
  return (
    <>
      <span className="absolute top-4 left-4 drop-shadow-md">
        <MedalIcon tier={tier} size={32} />
      </span>
      <span className="absolute top-4 right-4 drop-shadow-md" aria-hidden>
        <MedalIcon tier={tier} size={32} />
      </span>
    </>
  );
}

function GameOverRestartControl({
  onRestart,
  pending,
  restartLabel,
  pendingRestartLabel,
  restartLocked,
}: Pick<
  GameOverBannerProps,
  "onRestart" | "pending" | "restartLabel" | "pendingRestartLabel" | "restartLocked"
>) {
  if (restartLocked) {
    return (
      <PremiumUpgradePopover
        triggerLabel={restartLabel ?? "Restart"}
        description={REPLAY_REQUIRES_PREMIUM_COPY}
        className="w-full"
      />
    );
  }

  return (
    <Button onClick={onRestart} disabled={pending} size="lg" className="w-full">
      {pending ? pendingRestartLabel : restartLabel}
    </Button>
  );
}

function GameOverBannerActions({
  status,
  mapId,
  onRestart,
  pending,
  restartLabel,
  pendingRestartLabel,
  restartLocked,
  showLeaderboardLink,
}: Pick<
  GameOverBannerProps,
  | "status"
  | "mapId"
  | "onRestart"
  | "pending"
  | "restartLabel"
  | "pendingRestartLabel"
  | "restartLocked"
  | "showLeaderboardLink"
>) {
  const router = useRouter();

  const handleBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      void router.navigate({ to: "/" });
    }
  };

  return (
    <CardFooter className="flex flex-col gap-3">
      <GameOverRestartControl
        onRestart={onRestart}
        pending={pending}
        restartLabel={restartLabel}
        pendingRestartLabel={pendingRestartLabel}
        restartLocked={restartLocked}
      />
      {status === "victory" && showLeaderboardLink ? (
        <Link to="/leaderboard/$mapId" params={{ mapId }} preload={false} className="w-full">
          <Button variant="accent" size="lg" className="w-full">
            Leaderboard
          </Button>
        </Link>
      ) : null}
      <Button variant="outline" size="lg" className="w-full" onClick={handleBack}>
        ← Back
      </Button>
    </CardFooter>
  );
}

function GameOverBanner({
  status,
  dayNumber,
  mapId,
  state,
  onRestart,
  pending,
  restartLabel = "Restart",
  pendingRestartLabel = "Starting…",
  restartLocked = false,
  showLeaderboardLink = true,
  extraContent,
  medalTier,
}: GameOverBannerProps) {
  const headline = status === "victory" ? "Victory" : "Defeat";
  const subtext = victorySubtext({ status, dayNumber, state });

  return (
    <Card className="pointer-events-auto relative shadow-2xl">
      {status === "victory" ? <VictoryMedalCorners tier={medalTier} /> : null}
      <CardHeader className="text-center">
        <CardTitle
          className={cn(
            "text-3xl font-semibold",
            status === "victory" ? "text-primary" : "text-destructive",
          )}
        >
          {headline}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-center">{subtext}</p>
        {extraContent}
      </CardContent>
      <GameOverBannerActions
        status={status}
        mapId={mapId}
        onRestart={onRestart}
        pending={pending}
        restartLabel={restartLabel}
        pendingRestartLabel={pendingRestartLabel}
        restartLocked={restartLocked}
        showLeaderboardLink={showLeaderboardLink}
      />
    </Card>
  );
}

export function GameOverDialog(props: GameOverBannerProps) {
  const headline = props.status === "victory" ? "Victory" : "Defeat";

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 border-0 bg-transparent p-0 shadow-none ring-0 sm:max-w-sm"
      >
        <DialogTitle className="sr-only">Game over — {headline}</DialogTitle>
        <GameOverBanner {...props} />
      </DialogContent>
    </Dialog>
  );
}
